import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import { Loader2, Lock } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  listQuoteFeeRates,
  updateQuoteFeeRate,
  type QuoteFeeRateRow,
} from "@/services/platformQuoteFeeRatesService";

/** Editable copy of one row, kept as strings so a half-typed "1." does not snap to 1. */
interface Draft {
  consumerFeePercent: string;
  professionalCommissionPercent: string;
}

function toDraft(row: QuoteFeeRateRow): Draft {
  return {
    consumerFeePercent: String(row.consumerFeePercent),
    professionalCommissionPercent: String(row.professionalCommissionPercent),
  };
}

function isValidPercent(raw: string): boolean {
  const n = Number(raw);
  return raw.trim() !== "" && Number.isFinite(n) && n >= 0 && n <= 100;
}

/**
 * What Gestionalo takes on a bespoke request, per discipline.
 *
 * Lives on the pricing page because that is where an admin looks for "what do
 * we charge", but deliberately its own card: these rates are per DISCIPLINE
 * and apply to work the platform does not price, unlike every product row.
 */
export function QuoteFeeRatesCard(): JSX.Element {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [drafts, setDrafts] = useState<Record<string, Draft>>({});

  const ratesQuery = useQuery({
    queryKey: ["platform", "quote-fee-rates"],
    queryFn: listQuoteFeeRates,
  });

  // Server values seed the inputs, and re-seed after a save, so the screen
  // shows what was stored rather than what was typed.
  useEffect(() => {
    if (!ratesQuery.data) return;
    setDrafts(Object.fromEntries(ratesQuery.data.map((row) => [row.discipline, toDraft(row)])));
  }, [ratesQuery.data]);

  const saveMutation = useMutation({
    mutationFn: updateQuoteFeeRate,
    onSuccess: () => {
      toast.success(t("platform.quoteFeeRates.saveSuccess"));
      void queryClient.invalidateQueries({ queryKey: ["platform", "quote-fee-rates"] });
    },
    onError: (err: unknown) => {
      // The backend refuses a notary commission with a reason worth reading,
      // so its message is shown instead of a generic failure.
      const serverMessage =
        err !== null &&
        typeof err === "object" &&
        "response" in err &&
        typeof (err as { response?: { data?: { message?: unknown } } }).response?.data?.message ===
          "string"
          ? (err as { response: { data: { message: string } } }).response.data.message
          : null;
      toast.error(serverMessage ?? t("platform.quoteFeeRates.saveError"));
    },
  });

  const rows = ratesQuery.data ?? [];

  return (
    <Card className="mt-4 shadow-sm">
      <CardContent className="p-6">
        <h2 className="text-sm font-semibold text-foreground">
          {t("platform.quoteFeeRates.title")}
        </h2>
        <p className="mt-1 text-xs text-muted-foreground">{t("platform.quoteFeeRates.hint")}</p>

        {ratesQuery.isLoading ? (
          <div className="mt-4 flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin motion-reduce:animate-none" />
            {t("platform.quoteFeeRates.loading")}
          </div>
        ) : (
          <div className="mt-4 space-y-4">
            {rows.map((row) => {
              const draft = drafts[row.discipline] ?? toDraft(row);
              /*
                FROM THE REGISTRY, not from the slug. This was
                `row.discipline === "notary"`, which locked the field for
                exactly one profession and left it editable for every one
                registered later — so an admin could type a commission the
                server then refused, for a profession whose ethics code forbids
                it just as the notary's does.
              */
              const commissionLocked = !row.commissionPermitted;
              const isSaving =
                saveMutation.isPending && saveMutation.variables?.discipline === row.discipline;
              const isDirty =
                draft.consumerFeePercent !== String(row.consumerFeePercent) ||
                draft.professionalCommissionPercent !== String(row.professionalCommissionPercent);
              const canSave =
                isDirty &&
                !isSaving &&
                isValidPercent(draft.consumerFeePercent) &&
                isValidPercent(draft.professionalCommissionPercent);

              const setField = (field: keyof Draft, value: string): void =>
                setDrafts((prev) => ({
                  ...prev,
                  [row.discipline]: { ...draft, [field]: value },
                }));

              return (
                <div key={row.discipline} className="rounded-lg border border-border p-4">
                  <h3 className="text-sm font-medium text-foreground">
                    {/*
                      The registry names the row. A per-slug translation key
                      would mean a profession added by an admin renders as its
                      own key until someone ships a code change.
                    */}
                    {row.displayName ?? row.discipline}
                  </h3>

                  <div className="mt-3 grid gap-4 sm:grid-cols-2">
                    <div>
                      <Label htmlFor={`consumer-${row.discipline}`}>
                        {t("platform.quoteFeeRates.consumerFeeLabel")}
                      </Label>
                      <Input
                        id={`consumer-${row.discipline}`}
                        type="number"
                        min={0}
                        max={100}
                        step="0.01"
                        className="mt-1"
                        value={draft.consumerFeePercent}
                        onChange={(e) => setField("consumerFeePercent", e.target.value)}
                      />
                    </div>

                    <div>
                      <Label htmlFor={`commission-${row.discipline}`}>
                        {t("platform.quoteFeeRates.commissionLabel")}
                      </Label>
                      <Input
                        id={`commission-${row.discipline}`}
                        type="number"
                        min={0}
                        max={100}
                        step="0.01"
                        className="mt-1"
                        value={draft.professionalCommissionPercent}
                        // Disabled rather than merely defaulted to 0: the
                        // backend refuses a notary commission outright, and an
                        // editable field that always errors is worse than one
                        // that explains itself.
                        disabled={commissionLocked}
                        onChange={(e) => setField("professionalCommissionPercent", e.target.value)}
                      />
                      {commissionLocked ? (
                        <p className="mt-1.5 flex items-start gap-1.5 text-xs text-muted-foreground">
                          <Lock className="mt-0.5 h-3 w-3 shrink-0" aria-hidden />
                          {t("platform.quoteFeeRates.commissionLocked", {
                            profession: row.displayName ?? row.discipline,
                          })}
                        </p>
                      ) : null}
                    </div>
                  </div>

                  <div className="mt-3 flex justify-end">
                    <Button
                      type="button"
                      size="sm"
                      disabled={!canSave}
                      onClick={() =>
                        saveMutation.mutate({
                          discipline: row.discipline,
                          consumerFeePercent: Number(draft.consumerFeePercent),
                          professionalCommissionPercent: Number(draft.professionalCommissionPercent),
                        })
                      }
                    >
                      {isSaving ? (
                        <Loader2 className="mr-2 h-4 w-4 animate-spin motion-reduce:animate-none" />
                      ) : null}
                      {t("platform.quoteFeeRates.save")}
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
